import OrderStatusBadge from '@/components/shared/OrderStatusBadge'
import { formatDate, formatDateTime } from '@/lib/utils'
import type { OrderListItem, ProductItem } from '@/types/order'
import { Tooltip } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

export type OrderRow = Omit<OrderListItem, 'createdAt' | 'deliveryDate'> & {
  createdAt: Date
  deliveryDate: Date | null
}

export const columns: GridColDef<OrderRow>[] = [
  {
    field: 'orderNumber',
    headerName: 'Order Number',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value, row) => row.orderNumber ?? '-'
  },
  {
    field: 'userEmail',
    headerName: 'User Email',
    flex: 1,
    minWidth: 200,
    renderCell: (params: GridRenderCellParams<OrderRow, string>) => (
      <Tooltip title={params.value}>
        <span>{params.value ?? '-'}</span>
      </Tooltip>
    )
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 130,
    renderCell: (params: GridRenderCellParams<OrderRow, string>) => <OrderStatusBadge status={params.value ?? ''} />
  },
  {
    field: 'items',
    headerName: 'Items',
    flex: 1.5,
    minWidth: 200,
    sortable: false,
    renderCell: (params: GridRenderCellParams<OrderRow, ProductItem[]>) => {
      const itemNames = params.value?.map(item => item.name) ?? []
      const itemsPreview =
        itemNames.length > 2 ? `${itemNames.slice(0, 2).join(', ')} +${itemNames.length - 2}` : itemNames.join(', ')

      return (
        <Tooltip
          title={
            <div>
              {itemNames.map((name, index) => (
                <div key={`${name}-${index}`}>{name}</div>
              ))}
            </div>
          }
        >
          <span>{itemsPreview}</span>
        </Tooltip>
      )
    }
  },
  {
    field: 'shippingPostcode',
    headerName: 'Shipping Postcode',
    minWidth: 150,
    sortable: false, // Not sortable on the orders endpoint.
    valueGetter: (_value, row) => row.shippingPostcode ?? '-'
  },
  {
    field: 'totalAmount',
    headerName: 'Total',
    minWidth: 100,
    type: 'number',
    valueGetter: (value: number) => (typeof value === 'number' ? `£${value.toFixed(2)}` : '-')
  },
  {
    field: 'createdAt',
    headerName: 'Order Date',
    flex: 1,
    minWidth: 180,
    type: 'dateTime',
    valueFormatter: value => formatDateTime(value)
  },
  {
    field: 'deliveryDate',
    headerName: 'Delivery Date',
    flex: 1,
    minWidth: 180,
    type: 'dateTime',
    valueFormatter: value => (value ? formatDate(value) : '-')
  }
]

/**
 * Filterable but not rendered — all describe the medication requests inside an order, so an order
 * matches when one of its items does. The order `Status` column collapses every clinical stage onto
 * `wc-awaiting-decision`, so `Medication Request Status` is the only route to them.
 */
export const ORDER_FILTER_ONLY_COLUMNS: GridColDef[] = [
  { field: 'orderItemStatus', headerName: 'Medication Request Status' },
  { field: 'patientResponse', headerName: 'Patient Response', type: 'boolean' },
  { field: 'csNotes', headerName: 'CS Notes' },
  { field: 'pharmacistNotes', headerName: 'Pharmacist Notes' }
]
